import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { supabase } from '../../lib/supabase';
import { cn } from '../../lib/cn';
import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useOrg, orgQueryKey } from '../../app/useOrg';
import { useToast } from '../../components/Toast';
import styles from './settings.module.css';

const optionalNineDigits = z
  .string()
  .optional()
  .refine((v) => !v || /^\d{9}$/.test(v), '9 ספרות בלבד');

const orgSchema = z.object({
  name: z.string().min(1, 'שדה חובה').min(2, 'שם העסק קצר מדי').max(100, 'שם העסק ארוך מדי'),
  business_id: optionalNineDigits,
  vat_id: optionalNineDigits,
});
type OrgInput = z.infer<typeof orgSchema>;

interface OrgRowFull {
  id: string;
  name: string;
  business_id: string | null;
  vat_id: string | null;
  vat_rate: number;
  timezone: string;
  currency: string;
  locale: string;
  created_at: string;
  updated_at: string;
}

function formatDate(iso: string): string {
  try {
    return format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: he });
  } catch {
    return iso;
  }
}

export function OrganizationPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const { data: org, isLoading } = useOrg(orgId);
  const queryClient = useQueryClient();

  const isOwner = teamMember?.role === 'owner';
  const [meta, setMeta] = useState<OrgRowFull | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!orgId) return;
    let active = true;
    void supabase
      .from('organizations')
      .select('id, name, business_id, vat_id, vat_rate, timezone, currency, locale, created_at, updated_at')
      .eq('id', orgId)
      .maybeSingle<OrgRowFull>()
      .then(({ data }) => {
        if (active && data) setMeta(data);
      });
    return () => { active = false; };
  }, [orgId, org?.name]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<OrgInput>({
    resolver: zodResolver(orgSchema),
    defaultValues: { name: '', business_id: '', vat_id: '' },
  });

  useEffect(() => {
    if (meta) {
      reset({
        name: meta.name,
        business_id: meta.business_id ?? '',
        vat_id: meta.vat_id ?? '',
      });
    }
  }, [meta, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!orgId || !isOwner) return;
    const { error } = await supabase
      .from('organizations')
      .update({
        name: values.name,
        business_id: values.business_id?.length ? values.business_id : null,
        vat_id: values.vat_id?.length ? values.vat_id : null,
      })
      .eq('id', orgId);
    if (error) {
      toast.error('לא הצלחנו לשמור · נסה שוב');
      return;
    }
    toast.success('פרטי העסק עודכנו');
    await queryClient.invalidateQueries({ queryKey: orgQueryKey });
  });

  if (isLoading || !meta) return null;

  const disabled = !isOwner;

  return (
    <>
      <div className={styles.pageHead}>
        <h1>פרטי עסק</h1>
        <p className={styles.pageSub}>הפרטים שיופיעו בחשבוניות, הצעות מחיר וחתימות אימייל.</p>
      </div>

      {disabled ? (
        <p className={styles.notice}>רק בעלים יכולים לערוך פרטי עסק. ניתן לצפות בלבד.</p>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3>פרטים עיקריים</h3>
          <p>המידע הזה ייכתב על כל חשבונית והצעת מחיר.</p>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="org-name">שם העסק</label>
            <input
              id="org-name"
              type="text"
              className={cn(styles.input, errors.name && styles.invalid)}
              disabled={disabled}
              {...register('name')}
            />
            {errors.name ? <p className={styles.error}>{errors.name.message}</p> : null}
          </div>

          <div className={styles.row} style={{ marginBlockStart: 'var(--space-3)' }}>
            <div className={styles.field}>
              <label htmlFor="org-bizid">
                ע.מ. / ח.פ.
                <span className={styles.optional}>אופציונלי · 9 ספרות</span>
              </label>
              <input
                id="org-bizid"
                type="text"
                inputMode="numeric"
                dir="ltr"
                className={cn(styles.input, errors.business_id && styles.invalid)}
                disabled={disabled}
                {...register('business_id')}
              />
              {errors.business_id ? <p className={styles.error}>{errors.business_id.message}</p> : null}
            </div>
            <div className={styles.field}>
              <label htmlFor="org-vatid">
                מספר עוסק מורשה
                <span className={styles.optional}>אופציונלי · 9 ספרות</span>
              </label>
              <input
                id="org-vatid"
                type="text"
                inputMode="numeric"
                dir="ltr"
                className={cn(styles.input, errors.vat_id && styles.invalid)}
                disabled={disabled}
                {...register('vat_id')}
              />
              {errors.vat_id ? <p className={styles.error}>{errors.vat_id.message}</p> : null}
            </div>
          </div>

          <div className={styles.row} style={{ marginBlockStart: 'var(--space-3)' }}>
            <div className={styles.field}>
              <label htmlFor="org-vatrate">שיעור מע"מ</label>
              <input
                id="org-vatrate"
                type="text"
                className={styles.input}
                value={`${String(meta.vat_rate)}%`}
                disabled
                dir="ltr"
              />
              <p className={styles.notice}>הערך הזה קבוע ב-Phase 0 לפי החוק בישראל.</p>
            </div>
            <div className={styles.field}>
              <label htmlFor="org-currency">מטבע · שפה · אזור זמן</label>
              <input
                id="org-currency"
                type="text"
                className={styles.input}
                value={`${meta.currency} · ${meta.locale} · ${meta.timezone}`}
                disabled
                dir="ltr"
              />
              <p className={styles.notice}>עריכה זמינה ב-Phase 1.</p>
            </div>
          </div>

          <div className={styles.actions} style={{ marginBlockStart: 'var(--space-4)' }}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => { reset(); }}
              disabled={disabled || isSubmitting || !isDirty}
            >
              בטל
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={disabled || isSubmitting}>
              שמור
            </button>
          </div>
        </form>

        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>נוצר</span>
            <span className={styles.metaValue}>
              <span className="num">{formatDate(meta.created_at)}</span>
            </span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>עדכון אחרון</span>
            <span className={styles.metaValue}>
              <span className="num">{formatDate(meta.updated_at)}</span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
